import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';

/**
 * Hook personalizzato per ottenere i colori standardizzati per tutti i temi
 * Centralizza la logica dei colori per dark, light e brown themes
 */
export const useThemeColors = () => {
    const theme = useTheme();
    
    return useMemo(() => {
        const isDark = theme.palette.mode === 'dark';
        const isBrown = theme.palette.mode === 'light' && theme.palette.primary.main === '#d2a679';
        // SoulTech può usare #2A9D8F (nuovo) o #2D7A7F (vecchio)
        const isSoulTech = theme.palette.mode === 'light' && (theme.palette.primary.main === '#2A9D8F' || theme.palette.primary.main === '#2D7A7F');
        const isLight = theme.palette.mode === 'light' && theme.palette.primary.main !== '#d2a679' && theme.palette.primary.main !== '#2D7A7F' && theme.palette.primary.main !== '#2A9D8F';
        
        // Colori per tabelle
        const tableColors = (() => {
            if (isDark) {
                return {
                    header: theme.palette.background.paper,
                    rowEven: theme.palette.background.default,
                    rowOdd: theme.palette.action.hover,
                    hover: theme.palette.action.selected,
                    text: theme.palette.text.primary,
                    headerText: theme.palette.text.primary,
                    border: theme.palette.divider
                };
            } else if (isSoulTech) {
                return {
                    header: '#2A9D8F',
                    rowEven: '#F8FAFB',
                    rowOdd: '#EDF4F5',
                    hover: 'rgba(42, 157, 143, 0.12)',
                    text: '#264653',
                    headerText: '#FFFFFF',
                    border: 'rgba(38, 70, 83, 0.15)'
                };
            } else if (isBrown) {
                return {
                    header: '#8d6e63',
                    rowEven: '#eee0cb',
                    rowOdd: '#d7cdb8',
                    hover: '#c8b393',
                    text: '#5d4037',
                    headerText: '#ffffff',
                    border: '#a1887f'
                };
            } else { // Light theme
                return {
                    header: theme.palette.primary.main,
                    rowEven: theme.palette.background.paper,
                    rowOdd: theme.palette.grey[100],
                    hover: theme.palette.grey[200],
                    text: '#000000',
                    headerText: '#ffffff',
                    border: theme.palette.divider
                };
            }
        })();
        
        // Colori per card e contenitori
        const cardColors = (() => {
            if (isDark) {
                return {
                    background: theme.palette.background.paper,
                    surface: theme.palette.background.default,
                    text: theme.palette.text.primary,
                    secondaryText: theme.palette.text.secondary,
                    border: theme.palette.divider
                };
            } else if (isSoulTech) {
                return {
                    background: '#FFFFFF',
                    surface: '#F8FAFB',
                    text: '#264653',
                    secondaryText: '#4A6572',
                    border: 'rgba(38, 70, 83, 0.15)'
                };
            } else if (isBrown) {
                return {
                    background: '#eee0cb',
                    surface: '#f5f0e8',
                    text: '#5d4037',
                    secondaryText: '#8d6e63',
                    border: '#a1887f'
                };
            } else { // Light theme
                return {
                    background: theme.palette.background.paper,
                    surface: theme.palette.background.default,
                    text: theme.palette.text.primary,
                    secondaryText: theme.palette.text.secondary,
                    border: theme.palette.divider
                };
            }
        })();
        
        // Colori per stato (therapy, alerts, etc.)
        const statusColors = (() => {
            if (isDark) {
                return {
                    active: '#2e7d32',      // Verde scuro
                    suspended: '#ed6c02',   // Arancione
                    error: '#d32f2f',       // Rosso scuro
                    warning: '#f57c00',     // Arancione scuro
                    info: '#1976d2',        // Blu
                    success: '#388e3c'      // Verde
                };
            } else if (isSoulTech) {
                return {
                    active: '#5A9A5E',      // Verde naturale
                    suspended: '#C89A6B',   // Ocra caldo
                    error: '#B55A4A',       // Rosso terroso
                    warning: '#C89A6B',     // Ocra
                    info: '#2D7A7F',        // Teal
                    success: '#5A9A5E'      // Verde naturale
                };
            } else if (isBrown) {
                return {
                    active: '#43a047',      // Verde per tema marrone
                    suspended: '#fb8c00',   // Arancione per tema marrone
                    error: '#e53935',       // Rosso
                    warning: '#ff8f00',     // Arancione
                    info: '#1976d2',        // Blu
                    success: '#4caf50'      // Verde chiaro
                };
            } else { // Light theme
                return {
                    active: '#4caf50',      // Verde per tema chiaro
                    suspended: '#ff9800',   // Arancione per tema chiaro
                    error: '#f44336',       // Rosso
                    warning: '#ff9800',     // Arancione
                    info: '#2196f3',        // Blu chiaro
                    success: '#4caf50'      // Verde
                };
            }
        })();
        
        // Colori per bottoni e interazioni
        const interactionColors = (() => {
            if (isDark) {
                return {
                    primary: theme.palette.primary.main,
                    secondary: theme.palette.secondary.main,
                    outline: theme.palette.text.primary,
                    disabled: theme.palette.action.disabled,
                    hover: theme.palette.action.hover
                };
            } else if (isSoulTech) {
                return {
                    primary: '#2D7A7F',
                    secondary: '#A65E44',
                    outline: '#2D7A7F',
                    disabled: 'rgba(38, 42, 44, 0.26)',
                    hover: 'rgba(45, 122, 127, 0.08)'
                };
            } else if (isBrown) {
                return {
                    primary: '#d2a679',
                    secondary: '#8d6e63',
                    outline: '#8d6e63',
                    disabled: '#bdbdbd',
                    hover: '#c8b393'
                };
            } else { // Light theme
                return {
                    primary: theme.palette.primary.main,
                    secondary: theme.palette.secondary.main,
                    outline: theme.palette.primary.main,
                    disabled: theme.palette.action.disabled,
                    hover: theme.palette.action.hover
                };
            }
        })();
        
        // Colori per dialoghi e modali
        const dialogColors = (() => {
            if (isDark) {
                return {
                    background: theme.palette.background.paper,
                    backdrop: 'rgba(0, 0, 0, 0.8)',
                    border: theme.palette.divider,
                    headerBackground: theme.palette.background.default,
                    headerText: theme.palette.text.primary
                };
            } else if (isSoulTech) {
                return {
                    background: '#FAFAF7',
                    backdrop: 'rgba(38, 42, 44, 0.7)',
                    border: 'rgba(38, 42, 44, 0.2)',
                    headerBackground: '#2D7A7F',
                    headerText: '#FAFAF7'
                };
            } else if (isBrown) {
                return {
                    background: '#eee0cb',
                    backdrop: 'rgba(93, 64, 55, 0.3)',
                    border: '#a1887f',
                    headerBackground: '#d2a679',
                    headerText: '#ffffff'
                };
            } else { // Light theme
                return {
                    background: theme.palette.background.paper,
                    backdrop: 'rgba(0, 0, 0, 0.5)',
                    border: theme.palette.divider,
                    headerBackground: theme.palette.primary.main,
                    headerText: '#ffffff'
                };
            }
        })();
        
        return {
            // Informazioni tema
            theme: {
                mode: theme.palette.mode,
                isDark,
                isBrown,
                isSoulTech,
                isLight,
                name: isDark ? 'dark' : isSoulTech ? 'soultech' : isBrown ? 'brown' : 'light'
            },
            
            // Palette colori specifiche
            table: tableColors,
            card: cardColors,
            status: statusColors,
            interaction: interactionColors,
            dialog: dialogColors,
            
            // Colori diretti dal tema MUI
            primary: theme.palette.primary,
            secondary: theme.palette.secondary,
            background: theme.palette.background,
            text: theme.palette.text,
            action: theme.palette.action,
            
            // Funzioni di utilità
            getContrastText: (backgroundColor) => {
                if (isDark) return '#ffffff';
                if (isSoulTech) return '#FAFAF7';
                if (isBrown) return '#ffffff';
                return '#000000';
            },
            
            // Colori semantici globali
            semantic: {
                danger: isDark ? '#f44336' : isSoulTech ? '#B55A4A' : isBrown ? '#e53935' : '#d32f2f',
                warning: isDark ? '#ff9800' : isSoulTech ? '#C89A6B' : isBrown ? '#fb8c00' : '#f57c00',
                success: isDark ? '#4caf50' : isSoulTech ? '#5A9A5E' : isBrown ? '#43a047' : '#388e3c',
                info: isDark ? '#2196f3' : isSoulTech ? '#2D7A7F' : isBrown ? '#1976d2' : '#0277bd',
                error: {
                    main: isDark ? '#f44336' : isSoulTech ? '#B55A4A' : isBrown ? '#e53935' : '#d32f2f',
                    background: isDark ? '#ffebee' : isSoulTech ? '#f8e5e2' : isBrown ? '#ffebee' : '#ffebee',
                    text: isDark ? '#c62828' : isSoulTech ? '#8F4839' : isBrown ? '#c62828' : '#c62828',
                    border: isDark ? '#ef5350' : isSoulTech ? '#C97363' : isBrown ? '#ef5350' : '#ef5350'
                },
                warning: {
                    main: isDark ? '#ff9800' : isSoulTech ? '#C89A6B' : isBrown ? '#fb8c00' : '#f57c00',
                    background: isDark ? '#fff3e0' : isSoulTech ? '#f9f3ed' : isBrown ? '#fff3e0' : '#fff3e0',
                    text: isDark ? '#f57c00' : isSoulTech ? '#A67E55' : isBrown ? '#f57c00' : '#f57c00',
                    border: isDark ? '#ffab40' : isSoulTech ? '#D4AD85' : isBrown ? '#ffab40' : '#ffab40'
                },
                success: {
                    main: isDark ? '#4caf50' : isSoulTech ? '#5A9A5E' : isBrown ? '#43a047' : '#388e3c',
                    background: isDark ? '#e8f5e8' : isSoulTech ? '#eef5ee' : isBrown ? '#e8f5e8' : '#e8f5e8',
                    text: isDark ? '#2e7d32' : isSoulTech ? '#457848' : isBrown ? '#2e7d32' : '#2e7d32',
                    border: isDark ? '#66bb6a' : isSoulTech ? '#7BAE7E' : isBrown ? '#66bb6a' : '#66bb6a'
                },
                info: {
                    main: isDark ? '#2196f3' : isSoulTech ? '#2D7A7F' : isBrown ? '#1976d2' : '#0277bd',
                    background: isDark ? '#e3f2fd' : isSoulTech ? '#e8f2f3' : isBrown ? '#e3f2fd' : '#e3f2fd',
                    text: isDark ? '#0277bd' : isSoulTech ? '#1F5659' : isBrown ? '#0277bd' : '#0277bd',
                    border: isDark ? '#42a5f5' : isSoulTech ? '#44989D' : isBrown ? '#42a5f5' : '#42a5f5'
                }
            }
        };
    }, [theme]);
};

/**
 * Hook specifico per i colori delle tabelle (backward compatibility)
 * @deprecated Usa useThemeColors().table invece
 */
export const useTableColors = () => {
    const { table } = useThemeColors();
    return table;
};

/**
 * Hook specifico per i colori dei dialoghi (backward compatibility)
 * @deprecated Usa useThemeColors().dialog invece
 */
export const useDialogColors = () => {
    const { dialog } = useThemeColors();
    return dialog;
};

export default useThemeColors;